/**
 * UpdateFieldModal — inline "Update {label}" editor for a single field on a
 * single record, opened from a highlight card's hover Edit pencil.
 *
 * Renders the canonical CrmFieldValueInput (the same input every other edit
 * surface uses) prefilled with the field's current value, inside the shared
 * Dialog (header + sticky footer with Cancel / Save). Saving goes through
 * useFormState — the exact write path the slideout overview edit uses — so
 * properties, single-value associations, and many_to_many associations (edited
 * as a set add/remove diff) all behave identically to editing in the overview,
 * just scoped to this one field.
 *
 * Mounted only while open (the parent card gates on its editing state), so
 * useFormState seeds fresh from the record's current value each time.
 */

import { type ReactNode, useCallback, useMemo } from 'react';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import {
  Dialog,
  DialogTrigger,
  Modal,
  ModalOverlay,
} from '@/lib/base/overlays/modal';
import { CrmFieldValueInput } from '@/lib/crm/components/fields/CrmFieldValueInput';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';
import { useFormState } from '@/lib/crm/hooks/useFormState';
import { useStageTypeNames } from '@/lib/crm/hooks/useStageTypeNames';
import type { UpdatableField } from '@/lib/crm/hooks/useUpdatableFields';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

export interface UpdateFieldModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: number;
  object: CrmObject;
  /** The field to edit, resolved from useUpdatableFields by the calling card. */
  field: UpdatableField;
}

const EMPTY_REQUIRED_KEYS: ReadonlySet<string> = new Set();
const NO_PROPERTIES: Record<string, unknown> = {};
const NO_ASSOCIATIONS: Record<string, string | string[] | null> = {};

/**
 * Current association target ids for `name`, shaped by cardinality so the
 * canonical input renders correctly: a single id string for one/many-to-one,
 * an array for many_to_many (multi-select). Null when there are none.
 */
type AssociationCardinality = Extract<
  UpdatableField,
  { kind: 'association' }
>['cardinality'];

function currentAssociationValue(
  object: CrmObject,
  name: string,
  cardinality: AssociationCardinality
): string | string[] | null {
  const ids = (object.associations ?? [])
    .filter(a => a.association_type_name === name)
    .map(a => a.target_entity_id)
    .filter((id): id is string => Boolean(id));
  if (ids.length === 0) {
    return null;
  }
  return cardinality === 'many_to_many' ? ids : ids[0];
}

export function UpdateFieldModal({
  isOpen,
  onClose,
  orgId,
  object,
  field,
}: UpdateFieldModalProps) {
  const { user } = useAuthInfo();
  const { stageTypeNames } = useStageTypeNames(orgId);
  const { associationTypes, isLoading: associationTypesLoading } =
    useAssociationTypes(orgId);

  // Seed useFormState with just the one field being edited. The other side
  // (properties vs associations) stays empty so it contributes no changes.
  const initialProperties = useMemo<Record<string, unknown>>(
    () =>
      field.kind === 'property'
        ? {
            [field.name]:
              getPropertyValue(object.properties?.[field.name]) ?? null,
          }
        : NO_PROPERTIES,
    [field, object.properties]
  );
  const initialAssociations = useMemo<Record<string, string | string[] | null>>(
    () =>
      field.kind === 'association'
        ? {
            [field.name]: currentAssociationValue(
              object,
              field.name,
              field.cardinality
            ),
          }
        : NO_ASSOCIATIONS,
    [field, object]
  );

  // An emptied required property is sent as '' so the backend rejects the clear
  // (mirrors the overview edit). Optional cleared fields send null.
  const requiredPropertyKeys = useMemo<ReadonlySet<string>>(
    () =>
      field.kind === 'property' && field.schema.is_required
        ? new Set([field.name])
        : EMPTY_REQUIRED_KEYS,
    [field]
  );

  const handleSaveSuccess = useCallback(() => {
    toast.success('Field updated', { description: `${field.label} updated` });
    onClose();
  }, [field.label, onClose]);

  const {
    currentProperties,
    currentAssociations,
    updateProperty,
    updateAssociation,
    changeCount,
    saveChanges,
    isSaving,
    saveError,
  } = useFormState({
    orgId,
    userId: user?.id || 0,
    object,
    initialProperties,
    initialAssociations,
    requiredPropertyKeys,
    onSaveSuccess: handleSaveSuccess,
  });

  const associationType = useMemo<AssociationType | null>(() => {
    if (field.kind !== 'association') {
      return null;
    }
    return (
      associationTypes.find(at => at.association_type_name === field.name) ??
      null
    );
  }, [field, associationTypes]);

  const handlePropertyChange = useCallback(
    (value: unknown) => updateProperty(field.name, value),
    [updateProperty, field.name]
  );

  const handleAssociationChange = useCallback(
    (value: string | string[] | null) => updateAssociation(field.name, value),
    [updateAssociation, field.name]
  );

  const handleSave = useCallback(() => {
    void saveChanges();
  }, [saveChanges]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !isSaving) {
        onClose();
      }
    },
    [isSaving, onClose]
  );

  // Save is enabled once the value differs from what's on the record — matching
  // the overview's "N unsaved changes" gating. Clearing a field is a valid edit.
  const canSave = changeCount > 0 && !isSaving;

  const footer = useMemo(
    () => (
      <div className='flex flex-col items-stretch gap-2'>
        {saveError && (
          <p
            className='text-xs text-[var(--color-text-error-primary)]'
            role='alert'
          >
            {saveError}
          </p>
        )}
        <div className='flex items-center justify-end gap-3'>
          <Button
            color='secondary'
            size='md'
            onClick={onClose}
            isDisabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            color='primary'
            size='md'
            onClick={handleSave}
            isDisabled={!canSave}
            isLoading={isSaving}
          >
            Save
          </Button>
        </div>
      </div>
    ),
    [onClose, isSaving, handleSave, canSave, saveError]
  );

  let fieldInput: ReactNode = null;
  if (field.kind === 'property') {
    fieldInput = (
      <CrmFieldValueInput
        kind='property'
        schema={field.schema}
        value={
          currentProperties[field.name] as
            | string
            | number
            | object
            | null
            | undefined
        }
        onChange={handlePropertyChange}
        disabled={isSaving}
      />
    );
  } else if (field.kind === 'association' && associationTypesLoading) {
    // Association types still loading — show a placeholder instead of a blank
    // body so Save isn't offered against an unmounted editor.
    fieldInput = <LoadingIndicator type='line-simple' size='sm' />;
  } else if (associationType) {
    fieldInput = (
      <CrmFieldValueInput
        kind='association'
        associationType={associationType}
        value={currentAssociations[field.name]}
        onChange={handleAssociationChange}
        orgId={orgId}
        stageTypeNames={stageTypeNames}
        sourceEntityId={object.entity_id}
        disabled={isSaving}
        disableAutoSelect
      />
    );
  }

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
      <ModalOverlay isDismissable={!isSaving}>
        <Modal className='sm:max-w-md'>
          <Dialog
            title={`Update ${field.label}`}
            instance='update-field'
            isCloseDisabled={isSaving}
            footer={footer}
          >
            <div className='pb-2'>{fieldInput}</div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
}
